import React from "react";
import { Check, Phone, Mail, Star, Leaf, Mountain, Droplets, Sun, Moon, Utensils, Wifi, Car, Shield } from "lucide-react";
import Section from "../ui/Section";
import GalleryBlock from "../ui/GalleryBlock";
import PremiumButton from "../ui/PremiumButton";
import { IMAGES } from "../../pages/stayImages";
import { CONTACT } from "../../lib/email";

// Booking email with pre-filled content
const bookingEmailUrl = () => {
  const email = "bookingglowlawns@gmail.com";
  const subject = "Booking Inquiry - Glow Lawns Moonrise Valley";
  const body = `Hello Glow Lawns Team,

I would like to inquire about booking accommodation at your resort.

--- BOOKING DETAILS ---
Check-in Date: 
Check-out Date: 
Number of Guests: 
Accommodation Type: [ ] Tree House Cottage  [ ] Stone House  [ ] Mud House

--- GUEST INFORMATION ---
Name: 
Phone: 
Email: 

--- SPECIAL REQUESTS ---


Looking forward to your response.

Best regards`;
  
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

// Luxury badge component
const LuxuryBadge = ({ children, variant = "default" }) => {
  const variants = {
    default: "border-white/20 text-white/70 bg-white/5",
    gold: "border-[#C9A227]/40 text-[#C9A227] bg-[#C9A227]/10",
    highlight: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
  };
  
  return (
    <span className={`px-3 py-1.5 text-xs tracking-wide rounded-full border backdrop-blur-sm ${variants[variant]}`}>
      {children}
    </span>
  );
};

// Simple amenity check item
const AmenityCheck = ({ children }) => (
  <div className="flex items-center gap-3 text-white/80 text-sm">
    <Check className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
    <span>{children}</span>
  </div>
);

// Section title component
const SectionTitle = ({ children, subtitle, centered = false }) => (
  <div className={`mb-10 ${centered ? 'text-center' : ''}`}>
    <h3 className="text-2xl md:text-4xl font-light text-white tracking-tight">{children}</h3>
    {subtitle && <p className={`mt-3 text-white/60 text-sm md:text-base max-w-2xl ${centered ? 'mx-auto' : ''}`}>{subtitle}</p>}
  </div>
);

// Price card component
const PriceCard = ({ title, price, unit, highlight, description }) => (
  <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${highlight ? 'border-[#C9A227]/50 bg-gradient-to-br from-[#C9A227]/15 to-[#C9A227]/5 shadow-lg shadow-[#C9A227]/10' : 'border-white/10 bg-white/5'}`}>
    {highlight && (
      <div className="text-[10px] uppercase tracking-widest text-[#C9A227] mb-2 flex items-center gap-1">
        <Star className="w-3 h-3" fill="currentColor" />
        Most Popular
      </div>
    )}
    <div className="text-white/70 text-sm mb-2">{title}</div>
    <div className="flex items-baseline gap-1 mb-2">
      <span className="text-3xl font-light text-white">{price}</span>
      <span className="text-white/50 text-sm">/ {unit}</span>
    </div>
    {description && <p className="text-xs text-white/50">{description}</p>}
  </div>
);

// Stat block component
const StatBlock = ({ value, label }) => (
  <div className="text-center p-4">
    <div className="text-3xl md:text-4xl font-light text-white mb-1">{value}</div>
    <div className="text-xs text-white/50 uppercase tracking-wider">{label}</div>
  </div>
);

// Feature highlight component
const FeatureHighlight = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4 p-5 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/[0.07] transition-colors">
    <div className="w-12 h-12 rounded-xl bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-[#C9A227]" />
    </div>
    <div>
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-white/60 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

// Accommodation section component
const AccommodationSection = ({ 
  title, 
  subtitle,
  description, 
  features, 
  stats, 
  images, 
  badges,
  pricing,
  reversed = false
}) => (
  <div className="py-16 md:py-20 border-t border-white/10">
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-start ${reversed ? '' : ''}`}>
      {/* Gallery */}
      <div className={reversed ? 'lg:order-2' : ''}>
        <GalleryBlock images={images} title={`${title} Gallery`} alt={title} />
      </div>
      
      {/* Details */}
      <div className={`space-y-6 ${reversed ? 'lg:order-1' : ''}`}>
        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          {badges.map((badge, i) => (
            <LuxuryBadge key={badge} variant={i === 0 ? 'gold' : 'default'}>
              {badge}
            </LuxuryBadge>
          ))}
        </div>
        
        {/* Title */}
        <div>
          <h3 className="text-3xl md:text-4xl font-light text-white mb-2">{title}</h3>
          {subtitle && <p className="text-[#C9A227] text-sm tracking-wide">{subtitle}</p>}
        </div>
        
        {/* Description */}
        <p className="text-white/70 leading-relaxed text-base md:text-lg">
          {description}
        </p>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 py-6 border-y border-white/10">
          {stats.map((stat) => (
            <div key={stat.label}>
              <div className="text-2xl md:text-3xl font-light text-white">{stat.value}</div>
              <div className="text-xs text-white/50">{stat.label}</div>
            </div>
          ))}
        </div>
        
        {/* Features */}
        <div className="grid grid-cols-2 gap-3">
          {features.map((feature) => (
            <AmenityCheck key={feature}>{feature}</AmenityCheck>
          ))}
        </div>

        {/* Pricing Section */}
        {pricing && (
          <div className="mt-6 p-6 rounded-2xl border border-[#C9A227]/30 bg-gradient-to-br from-[#C9A227]/10 to-[#C9A227]/5">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-[#C9A227]" fill="currentColor" />
              <h4 className="text-lg font-medium text-white">{pricing.title}</h4>
              {pricing.highlight && (
                <span className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#C9A227] text-[#111] rounded-full font-semibold">
                  Most Popular
                </span>
              )}
            </div>
            
            {/* Price List */}
            {pricing.rates && (
              <div className="space-y-2 mb-4">
                {pricing.rates.map((rate) => (
                  <div key={rate.label} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                    <span className="text-white/80 text-sm">{rate.label}</span>
                    <span className="text-white font-medium">{rate.price}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Entire Space Pricing */}
            {pricing.entireSpace && (
              <div className="py-3 px-4 rounded-xl bg-[#C9A227]/20 border border-[#C9A227]/30 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium">Entire Space</span>
                  <span className="text-2xl font-light text-[#C9A227]">{pricing.entireSpace}</span>
                </div>
                <p className="text-xs text-white/60 mt-1">Maximum occupancy</p>
              </div>
            )}

            {/* Additional Info */}
            {pricing.additionalGuest && (
              <p className="text-sm text-white/70 mb-2">
                <span className="text-white font-medium">Additional Guest:</span> {pricing.additionalGuest}
              </p>
            )}
            
            <p className="text-xs text-white/50 italic">
              * Prices may vary during peak seasons and holidays. Contact us for exact rates.
            </p>
          </div>
        )}

        {/* Book via Email Button */}
        <div className="flex flex-wrap gap-3 pt-4">
          <a
            href={bookingEmailUrl()}
            data-testid={`email-${title.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A227]/20 text-[#C9A227] text-sm font-medium hover:bg-[#C9A227]/30 transition-all hover:scale-105"
          >
            <Mail className="w-4 h-4" />
            Book via Email
          </a>
        </div>
        
        {/* Booking Phones */}
        <div className="flex flex-wrap items-center gap-2 text-sm text-white/50">
          <Phone className="w-4 h-4 text-[#C9A227]" />
          <span>Booking Helpline:</span>
          <a href={`tel:${CONTACT.primaryPhone.replace(/-/g, "")}`} className="text-white hover:text-[#C9A227] transition">
            {CONTACT.primaryPhone}
          </a>
          <span className="text-white/30">|</span>
          <a href={`tel:${CONTACT.supportPhone.replace(/-/g, "")}`} className="text-white hover:text-[#C9A227] transition">
            {CONTACT.supportPhone}
          </a>
        </div>
      </div>
    </div>
  </div>
);

// Amenity item component
const AmenityItem = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 text-white/80 text-sm group">
    <div className="w-8 h-8 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227]/20 transition-colors">
      {Icon ? <Icon className="w-4 h-4 text-[#C9A227]" /> : <Check className="w-4 h-4 text-[#C9A227]" />}
    </div>
    <span>{children}</span>
  </div>
);

const StaySection = () => {
  return (
    <section id="stay" className="scroll-mt-20" data-testid="stay-section">
      {/* Section Header */}
      <Section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 mb-8">
            <Leaf className="w-4 h-4 text-[#C9A227]" />
            <span className="text-xs uppercase tracking-widest text-[#C9A227]">Kavilumpara's Premier Nature Retreat</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-light text-white mb-6 leading-tight" data-testid="stay-section-title">
            Stay
          </h2>
          
          <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-12">
            Nestled in the heart of Kavilumpara's misty hills, Glow Lawns Moonrise Valley offers an extraordinary 
            escape where each accommodation has been thoughtfully designed to immerse you in nature while 
            providing every modern comfort. This is not just a stay—it's a transformation.
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
            <StatBlock value="6" label="Luxury Units" />
            <StatBlock value="31" label="Max Guests" />
            <StatBlock value="360°" label="Valley Views" />
            <StatBlock value="24/7" label="Personalized Service" />
          </div>
        </div>
      </Section>

      {/* Accommodation Overview Cards */}
      <Section className="py-16 border-t border-white/10">
        <SectionTitle 
          subtitle="Three distinct ways to experience Moonrise Valley, each offering its own unique character and charm."
          centered
        >
          Our Accommodations
        </SectionTitle>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="group p-8 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
            <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Mountain className="w-7 h-7 text-[#C9A227]" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">Premium Tree House Cottages</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Elevated, private, and surrounded by panoramic valley and waterfall views. Our signature experience.
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-3 py-1 rounded-full bg-[#C9A227]/10 text-[#C9A227] border border-[#C9A227]/20">4 Cottages</span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white/70">Up to 20 guests</span>
            </div>
          </div>
          
          <div className="group p-8 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
            <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Shield className="w-7 h-7 text-[#C9A227]" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">Stone House with Private Pool</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Refined privacy with private pool, 2 rooms, and tranquil ambience. Perfect for those who value quiet luxury.
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-3 py-1 rounded-full bg-[#C9A227]/10 text-[#C9A227] border border-[#C9A227]/20">2 Rooms</span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white/70">Up to 5 guests</span>
            </div>
          </div>
          
          <div className="group p-8 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
            <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Sun className="w-7 h-7 text-[#C9A227]" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">Mud House Retreat</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Earth-toned textures, 3 rooms with king beds, authentic nature living. A grounding experience.
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-3 py-1 rounded-full bg-[#C9A227]/10 text-[#C9A227] border border-[#C9A227]/20">3 Rooms</span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white/70">Up to 6 guests</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Tree House Section */}
      <Section>
        <AccommodationSection
          title="Premium Tree House Cottages"
          subtitle="Our Signature Experience"
          description="Elevated above the forest floor, each tree house offers a peaceful, cinematic retreat with uninterrupted views of waterfalls, valleys, and mist-covered mountains. Crafted from natural coconut wood with reinforced steel frameworks, these architectural marvels represent the perfect fusion of nature-inspired materials and structural elegance."
          badges={["Signature Stay", "Valley View", "Waterfall Views", "Forest Deck"]}
          stats={[
            { value: "4", label: "Premium Cottages" },
            { value: "5", label: "Guests per Cottage" },
            { value: "20", label: "Total Capacity" }
          ]}
          features={[
            "1 Master Bed Space with Queen-Size Bed",
            "1 Nest Bed Room with Bed Space for 2",
            "1 Private Balcony Wooden Deck",
            "Forest View, Panoramic Valley Views, Waterfall Views",
            "Modern Bathroom",
            "Air Conditioning",
            "24/7 Hot Water"
          ]}
          images={IMAGES.treehouse}
          pricing={{
            title: "Rates & Packages",
            highlight: true,
            rates: [
              { label: "Couple Stay", price: "₹4,999 / night" },
              { label: "Triple Occupancy", price: "₹5,999 / night" },
              { label: "4-Guest Sharing", price: "₹6,999 / night" },
              { label: "5-Guest Sharing (Max)", price: "₹7,999 / night" }
            ],
            additionalGuest: "₹1,000 per person per night"
          }}
        />
      </Section>

      {/* Stone House Section */}
      <Section>
        <AccommodationSection
          title="Stone House with Private Pool Villa"
          subtitle="Private Sanctuary"
          description="A private and refined stay designed for guests who value quiet comfort, natural surroundings, and understated luxury. The Stone House offers architectural solidity combined with tranquil ambience, creating a serene sanctuary where you can disconnect from the world and reconnect with nature."
          badges={["Private Retreat", "Private Pool", "2 Rooms"]}
          stats={[
            { value: "2", label: "Rooms" },
            { value: "2", label: "King Size Beds" },
            { value: "5", label: "Max Guests" }
          ]}
          features={[
            "Private Pool",
            "Complete Privacy",
            "2 King Size Beds",
            "Nature Immersion",
            "Air Conditioning",
            "Premium Bedding"
          ]}
          images={IMAGES.stonevilla}
          reversed={true}
          pricing={{
            title: "Rates & Packages",
            entireSpace: "₹7,999 / night"
          }}
        />
      </Section>

      {/* Mud House Section */}
      <Section>
        <AccommodationSection
          title="Mud House Retreat"
          subtitle="Authentic Earth Living"
          description="A warm, grounding living experience blending rustic character with modern convenience. The Mud House offers earth-toned textures and a warm atmosphere that creates an authentic connection to nature. This is where traditional building techniques meet contemporary comfort."
          badges={["Rustic Charm", "3 Rooms", "Family Friendly"]}
          stats={[
            { value: "3", label: "Rooms" },
            { value: "3", label: "King Size Beds" },
            { value: "6", label: "Max Guests" }
          ]}
          features={[
            "3 King Size Beds",
            "Spacious Layout",
            "Family Friendly",
            "Garden Views",
            "Modern Comfort",
            "Traditional Charm"
          ]}
          images={IMAGES.mudhouse}
          pricing={{
            title: "Rates & Packages",
            entireSpace: "₹6,999 / night"
          }}
        />
      </Section>

      {/* What's Included */}
      <Section className="py-16 border-t border-white/10">
        <SectionTitle centered>What's Included in Every Stay</SectionTitle>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          <FeatureHighlight 
            icon={Utensils}
            title="Kerala Breakfast"
            description="Start your day with authentic, complimentary Kerala cuisine"
          />
          <FeatureHighlight 
            icon={Droplets}
            title="24/7 Hot Water"
            description="Geyser-fitted bathrooms for your comfort anytime"
          />
          <FeatureHighlight 
            icon={Wifi}
            title="Connectivity"
            description="Stay connected with reliable mobile network coverage"
          />
          <FeatureHighlight 
            icon={Car}
            title="Free Parking"
            description="Secure parking space for your vehicle"
          />
        </div>
      </Section>

      {/* Full Amenities Grid */}
      <Section className="py-16 border-t border-white/10">
        <SectionTitle 
          subtitle="Every comfort thoughtfully provided for an unforgettable stay"
          centered
        >
          Complete Amenities
        </SectionTitle>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
          <AmenityItem icon={Moon}>Master Bedroom with Queen-Size Bed</AmenityItem>
          <AmenityItem>Premium Quilts & Blankets</AmenityItem>
          <AmenityItem>Air-Conditioned Interiors</AmenityItem>
          <AmenityItem>Ceiling Fan</AmenityItem>
          <AmenityItem>Television</AmenityItem>
          <AmenityItem>Electric Kettle</AmenityItem>
          <AmenityItem>Curated Toiletries Kit</AmenityItem>
          <AmenityItem icon={Droplets}>24×7 Hot Water (Geyser Fitted)</AmenityItem>
          <AmenityItem>Reliable Electricity Backup</AmenityItem>
          <AmenityItem>Complimentary Drinking Water</AmenityItem>
          <AmenityItem>Tea/Coffee/Milk Setup</AmenityItem>
          <AmenityItem>In-Room Beverage Service</AmenityItem>
        </div>
      </Section>

      {/* Views & Surroundings */}
      <Section className="py-20 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 mb-8">
            <Mountain className="w-4 h-4 text-[#C9A227]" />
            <span className="text-xs uppercase tracking-widest text-[#C9A227]">The Defining Highlight</span>
          </div>
          
          <h3 className="text-3xl md:text-4xl font-light text-white mb-6">
            Views That Take Your Breath Away
          </h3>
          
          <p className="text-white/60 text-base leading-relaxed max-w-2xl mx-auto mb-10">
            The defining highlight of Moonrise Valley is its unforgettable scenery. Every accommodation 
            offers perspectives that open into nature's grandest theatre.
          </p>
          
          <div className="grid sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
            <AmenityCheck>Mesmerizing waterfall vistas</AmenityCheck>
            <AmenityCheck>Mist-covered mountain ranges</AmenityCheck>
            <AmenityCheck>Lush green forest landscapes</AmenityCheck>
            <AmenityCheck>Serene valley perspectives</AmenityCheck>
            <AmenityCheck>Dramatic sunrise & sunset views</AmenityCheck>
            <AmenityCheck>Clear night sky stargazing</AmenityCheck>
          </div>
        </div>
      </Section>

      {/* Architectural Character */}
      <Section className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <SectionTitle centered>Architectural Character</SectionTitle>
          
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div className="p-6 rounded-xl border border-white/10 bg-white/5">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#C9A227]/10 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-[#C9A227]" />
              </div>
              <h4 className="text-white font-medium mb-2">Natural Materials</h4>
              <p className="text-white/60 text-sm">Built using natural coconut wood with traditional techniques</p>
            </div>
            <div className="p-6 rounded-xl border border-white/10 bg-white/5">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#C9A227]/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#C9A227]" />
              </div>
              <h4 className="text-white font-medium mb-2">Reinforced Structure</h4>
              <p className="text-white/60 text-sm">Steel frameworks ensure safety and durability</p>
            </div>
            <div className="p-6 rounded-xl border border-white/10 bg-white/5">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#C9A227]/10 flex items-center justify-center">
                <Star className="w-6 h-6 text-[#C9A227]" />
              </div>
              <h4 className="text-white font-medium mb-2">Refined Finish</h4>
              <p className="text-white/60 text-sm">Precision-crafted, polished cottage structures</p>
            </div>
          </div>
        </div>
      </Section>
    </section>
  );
};

export default StaySection;
